import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Empty, FloatButton, Input, Spin, Tooltip, Typography, message as antdMessage } from 'antd';
import { CloseOutlined, CustomerServiceOutlined, DeleteOutlined, ReloadOutlined, RobotOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { HttpService } from '../../services/http.service';
import jinxiaozhiImg from '../../assets/img/jinxiaozhi.png';
import './index.scoped.scss';

const { Text } = Typography;

type ChatRole = 'user' | 'assistant';

interface ChatMessage {
    id: string;
    role: ChatRole;
    content: string;
    loading?: boolean;
}

interface OnlineChatWidgetProps {
    url: string;
}

const WELCOME_MESSAGE = '您好！欢迎访问金智塔官网，我是智能客服金小智，请问有什么可以帮您？';
const FIRST_REPLY_MESSAGE = '请您留下公司名称和联系方式，感谢您的配合！';
const ACTION_BUTTON_SIZE = 40;
const MIN_CONTENT_LENGTH = 0;
const MAX_CONTENT_LENGTH = 2000;

const createMessageId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const OnlineChatWidget = (_props: OnlineChatWidgetProps) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            id: createMessageId(),
            role: 'assistant',
            content: WELCOME_MESSAGE
        }
    ]);
    const [sending, setSending] = useState(false);
    const messageBodyRef = useRef<HTMLDivElement | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const sessionIdRef = useRef(createMessageId());
    const hasUserSentRef = useRef(false);

    useEffect(() => {
        const body = messageBodyRef.current;
        if (body) {
            body.scrollTop = body.scrollHeight;
        }
    }, [messages, open]);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const updateAssistantMessage = (messageId: string, content: string, loading: boolean) => {
        setMessages((prevMessages) => {
            return prevMessages.map((item) => {
                if (item.id !== messageId) {
                    return item;
                }

                return {
                    ...item,
                    content,
                    loading
                };
            });
        });
    };

    const requestAnswer = async (question: string) => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        const [err, res] = await HttpService('POST', '/conversation/addMessage', {
            sessionId: sessionIdRef.current,
            content: question
        });

        if (err) {
            throw new Error('在线问答请求失败');
        }

        return typeof res?.answer === 'string' ? res.answer : '';
    };

    const handleSend = async () => {
        const question = inputValue.trim();
        if (!question || sending) {
            return;
        }
        if (question.length < MIN_CONTENT_LENGTH) {
            antdMessage.warning(`请输入至少${MIN_CONTENT_LENGTH}个字`);
            return;
        }
        if (question.length > MAX_CONTENT_LENGTH) {
            antdMessage.warning(`输入内容不能超过${MAX_CONTENT_LENGTH}个字`);
            return;
        }

        const userMessage: ChatMessage = {
            id: createMessageId(),
            role: 'user',
            content: question
        };
        const assistantMessage: ChatMessage = {
            id: createMessageId(),
            role: 'assistant',
            content: '',
            loading: true
        };
        const nextMessages = [...messages, userMessage, assistantMessage];

        setMessages(nextMessages);
        setInputValue('');
        setSending(true);

        try {            
            if (!hasUserSentRef.current) {
                hasUserSentRef.current = true;
                updateAssistantMessage(assistantMessage.id, FIRST_REPLY_MESSAGE, false);
            } else {
                const reply = await requestAnswer(question);
                updateAssistantMessage(assistantMessage.id, reply || '暂无回复，请稍后再试。', false);
            }
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                updateAssistantMessage(assistantMessage.id, '服务暂时不可用，请稍后再试。', false);
                antdMessage.error('在线问答请求失败');
            }
        } finally {
            setSending(false);
        }
    };

    const handleClear = () => {
        abortControllerRef.current?.abort();
        setSending(false);
        hasUserSentRef.current = false;
        sessionIdRef.current = createMessageId();
        setMessages([
            {
                id: createMessageId(),
                role: 'assistant',
                content: WELCOME_MESSAGE
            }
        ]);
    };

    return (
        <>            
            <Tooltip placement="left" title="在线问答">
                <img
                    src={jinxiaozhiImg}
                    className='online-chat-widget-icon'
                    alt="在线问答"
                    onClick={() => setOpen(true)}                        
                />
            </Tooltip>                            
            {open && (
                <div
                    style={{
                        position: 'fixed',
                        right: -20,
                        bottom: 0,
                        zIndex: 1000,
                        width: 400,
                        height: 600,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: 12,
                        background: '#eef6ff',
                        boxShadow: '0 16px 40px rgba(15, 63, 120, 0.24)'
                    }}
                >
                    <div
                        style={{
                            height: 46,
                            padding: '0 12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: '#fff',
                            background: 'linear-gradient(90deg, #1677ff 0%, #21a4ff 100%)'
                        }}
                    >
                        <div style={{ fontSize: 14, fontWeight: 600 }}>在线问答</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button type="text" size="small" icon={<DeleteOutlined />} onClick={handleClear} style={{ color: '#fff' }} />
                            {/* <Button type="text" size="small" icon={<ReloadOutlined />} onClick={handleClear} style={{ color: '#fff' }} /> */}
                        </div>
                    </div>
                    <div
                        ref={messageBodyRef}
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '18px 12px',
                            background: 'linear-gradient(180deg, #e8f4ff 0%, #f4f9ff 100%)'
                        }}
                    >
                        {messages.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无消息" />
                        ) : (
                            messages.map((item) => {
                                const isUser = item.role === 'user';

                                return (
                                    <div key={item.id} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 18 }}>
                                        <div style={{ display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 8 }}>
                                            <Avatar
                                                size={24}
                                                icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                                                style={{
                                                    flex: '0 0 auto',
                                                    color: isUser ? '#fff' : '#1677ff',
                                                    background: isUser ? '#bfe4ff' : '#d8f4ef'
                                                }}
                                            />
                                            <div
                                                style={{
                                                    maxWidth: 204,
                                                    minHeight: 20,
                                                    padding: '8px 12px',
                                                    borderRadius: isUser ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                                                    background: isUser ? 'transparent' : '#fff',
                                                    color: '#273142',
                                                    fontSize: 12,
                                                    lineHeight: '18px',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {item.loading && !item.content ? <Spin size="small" /> : <Text style={{ color: 'inherit', fontSize: 12 }}>{item.content}</Text>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    <div style={{ position: 'relative', padding: '12px 12px', background: '#fff' }}>
                        <Input.TextArea
                            value={inputValue}
                            placeholder="请输入您的问题"
                            autoSize={{ minRows: 1, maxRows: 3 }}
                            disabled={sending}
                            bordered={false}
                            maxLength={MAX_CONTENT_LENGTH}
                            style={{ padding: `4px ${ACTION_BUTTON_SIZE + 8}px 4px 0`, fontSize: 12, resize: 'none' }}
                            onChange={(event) => setInputValue(event.target.value)}
                            onPressEnter={(event) => {
                                if (!event.shiftKey) {
                                    event.preventDefault();
                                    handleSend();
                                }
                            }}
                        />
                        <Button
                            type="primary"
                            shape="circle"
                            icon={inputValue.trim() ? <SendOutlined /> : <CloseOutlined />}
                            loading={sending}
                            onClick={inputValue.trim() ? handleSend : () => setOpen(false)}
                            style={{ position: 'absolute', right: 10, bottom: 5, width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default OnlineChatWidget;
